import {TFunction} from 'i18next'
import moment from 'moment'

import {UnitType} from '../../common/enums'

const validateDecimalPrecision = (
  value: string,
  decimalPrecision: number,
  t: TFunction
): true | string => {
  const decimalIndex = value.indexOf('.')
  if (decimalIndex === -1) {
    return true
  }

  const decimals = value.substring(decimalIndex + 1).replace(/0+$/, '').length
  if (decimals > decimalPrecision) {
    return t('error.maxDecimalPlaces', {places: decimalPrecision})
  }

  return true
}

const validateNumericFormat = (value: string): boolean => {
  return /^[+-]?(\d{1,3}(,\d{3})*|\d*)(\.\d*)?$/.test(value)
}

const validateNumericValue = (
  floatValue: number,
  type: UnitType,
  t: TFunction,
  additionalValidationParams?: {
    minExclusiveValue?: number
    decimalPrecision?: number
    monthlyTime?: {month: number; year: number} | string
  }
): true | string => {
  if (Object.is(floatValue, -0) || floatValue < 0) {
    return t('error.minValue', {value: 0})
  }

  if (
    additionalValidationParams?.minExclusiveValue !== undefined &&
    floatValue <= additionalValidationParams?.minExclusiveValue
  ) {
    return t('error.minExclusiveValue', {value: additionalValidationParams?.minExclusiveValue})
  }

  if (type === UnitType.Time) {
    if (additionalValidationParams?.monthlyTime) {
      const totalHours = moment(additionalValidationParams?.monthlyTime).daysInMonth() * 24
      if (floatValue > totalHours) {
        return t('error.valueInARange', {min: 0, max: totalHours})
      }
    } else {
      if (floatValue > 24) {
        return t('error.valueInARange', {min: 0, max: 24})
      }
    }
  }

  return true
}

export const getIsValueFieldValid = (
  value: string | null | undefined,
  type: UnitType,
  t: TFunction,
  additionalValidationParams?: {
    minExclusiveValue?: number
    decimalPrecision?: number
    monthlyTime?: {month: number; year: number} | string
  }
) => {
  if (type === UnitType.Text) {
    return true
  }

  if (value === null || value === undefined || value === '') {
    return true
  }

  const trimmedValue = String(value).trim()

  if (trimmedValue === '') {
    return t('error.enterValidInput')
  }

  if (trimmedValue.toLowerCase().includes('e')) {
    return t('error.enterValidInput')
  }

  const floatValue = parseFloat(trimmedValue)

  if (isNaN(floatValue) || !validateNumericFormat(trimmedValue)) {
    return t('error.enterValidInput')
  }

  if (additionalValidationParams?.decimalPrecision !== undefined) {
    const validationResult = validateDecimalPrecision(
      trimmedValue,
      additionalValidationParams.decimalPrecision,
      t
    )
    if (typeof validationResult === 'string') {
      return validationResult
    }
  }

  return validateNumericValue(floatValue, type, t, additionalValidationParams)
}
